// The fast model's JSON has real failure modes (unescaped quotes, dropped
// brackets, token-cap truncation). This module carries the repair pipeline:
// structural bracket rebuild → targeted quote escaping → last-object salvage.

import { tidyProse } from "./text";

/** Strict extraction: strip code fences, slice the outermost object, JSON.parse. */
export function extractJSON(text: string): any {
  const t = text.trim().replace(/^```json\s*/i, "").replace(/^```\s*/, "").replace(/```\s*$/, "");
  const a = t.indexOf("{"), b = t.lastIndexOf("}");
  if (a === -1 || b === -1) throw new Error("no json");
  return JSON.parse(t.slice(a, b + 1));
}

/**
 * The fast model occasionally emits an unescaped quote inside a string value.
 * JSON.parse then fails just past the string: escape the quote that terminated
 * it early and retry, a few times.
 */
export function lenientParse(t: string): any {
  for (let i = 0; i < 8; i++) {
    try { return JSON.parse(t); } catch (e) {
      const m = /position (\d+)/.exec(String((e as Error).message));
      if (!m) return null;
      const q = t.lastIndexOf('"', +m[1]! - 1);
      if (q <= 0) return null;
      t = t.slice(0, q) + '\\"' + t.slice(q + 1);
    }
  }
  return null;
}

/**
 * It also drops/omits closing brackets (e.g. ending the doc "}}" with the
 * decisions array never closed) or stops mid-object at the token cap. Rebuild
 * the bracket structure: insert missing closers where a mismatched one
 * appears, and close whatever is still open at the end.
 */
const CTRL_ESC: Record<string, string> = { "\n": "\\n", "\r": "\\r", "\t": "\\t" };

export function structuralRepair(t: string): string {
  let out = "", inStr = false, esc = false;
  let lastSig = "";   // last significant (non-whitespace) char emitted outside strings
  const stack: string[] = [];
  // Truncation at the token cap often ends right after a key's colon or a
  // comma; closing brackets around that ("better":} ) is still invalid JSON.
  const patchDangling = () => {
    if (lastSig === ":") { out += "null"; lastSig = "l"; }
    else if (lastSig === ",") { out = out.replace(/,\s*$/, ""); lastSig = ""; }
  };
  for (const ch of t) {
    if (inStr) {
      // Raw control characters inside strings are invalid JSON the quote
      // repair can't fix — escape them in place.
      if (!esc && CTRL_ESC[ch]) { out += CTRL_ESC[ch]; continue; }
      out += ch;
      if (esc) esc = false;
      else if (ch === "\\") esc = true;
      else if (ch === '"') { inStr = false; lastSig = '"'; }
      continue;
    }
    if (ch === '"') { inStr = true; out += ch; continue; }
    if (ch === "{" || ch === "[") { stack.push(ch === "{" ? "}" : "]"); out += ch; lastSig = ch; continue; }
    if (ch === "}" || ch === "]") {
      patchDangling();
      while (stack.length && stack[stack.length - 1] !== ch) out += stack.pop();
      if (stack.length) stack.pop();
      out += ch;
      lastSig = ch;
      continue;
    }
    if (ch === ",") {
      if (lastSig === ",") continue;          // duplicate comma
      if (lastSig === ":") out += "null";     // dangling value
      out += ch;
      lastSig = ch;
      continue;
    }
    out += ch;
    if (!/\s/.test(ch)) lastSig = ch;
  }
  if (inStr) {
    if (esc) out = out.slice(0, -1);   // cut mid-escape — drop the dangling backslash
    out += '"';
  } else patchDangling();
  while (stack.length) out += stack.pop();
  return out;
}

/**
 * Neutralize escape sequences JSON.parse rejects: \u without 4 hex digits
 * (the model emits things like "\u26;") and \ before a non-escape char.
 * Turning them into literal backslashes preserves the surrounding string.
 */
export function fixInvalidEscapes(t: string): string {
  return t
    .replace(/\\u(?![0-9a-fA-F]{4})/g, "\\\\u")
    .replace(/\\(?!["\\/bfnrtu])/g, "\\\\");
}

/**
 * Token-level glitches the fast model emits mid-stream (observed in traces):
 * {"{"section" / ,"{"section" (doubled open tokens) and },section": (a
 * dropped {" after a comma).
 */
export function fixTokenGlitches(t: string): string {
  return t
    .replace(/\{\s*"\{\s*"\s*(section")/g, '{"$1')        // {"{"section" → {"section"
    .replace(/([,[])\s*"\{\s*"\s*(section")/g, '$1{"$2')  // ,"{"section" → ,{"section"
    .replace(/([}\]])\s*,\s*(section")\s*:/g, '$1,{"$2:') // },section": → },{"section":
    // Missing close-quote at an object boundary ("...load.},{"label":).
    // Guarded by the known next key so legit non-string boundaries are safe.
    .replace(/([^"\s{[,:])\s*\}\s*,\s*\{\s*"(label|tradeoff|section|content|k|v)"\s*:/g, '$1"},{"$2":')
    // Same, at the close of the array ("...risk.}],"recommend":).
    .replace(/([^"\s{[,:])\s*\}\s*\]\s*,\s*"(recommend|stakes|confidence)"\s*:/g, '$1"}],"$2":');
}

/**
 * Last-resort salvage for the flat {"updates":[{"section","content"}]} shape:
 * regex-extract every section/content pair from the raw text, so one corrupt
 * region doesn't lose every other update in the response.
 */
export function salvageSectionUpdates(raw: string): { section: string; content: string }[] {
  const out: { section: string; content: string }[] = [];
  const re = /"section"\s*:\s*"((?:[^"\\]|\\.)*)"\s*,\s*"content"\s*:\s*"((?:[^"\\]|\\.)*)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw))) {
    try {
      out.push({ section: JSON.parse(`"${m[1]}"`), content: JSON.parse(`"${m[2]}"`) });
    } catch { /* skip a pair whose escapes are beyond saving */ }
  }
  return out;
}

/** Repair + parse for any fast-model JSON (bracket rebuild → quote escapes). */
export function robustJSON(raw: string): any {
  const a = raw.indexOf("{");
  if (a === -1) return null;
  const end = raw.lastIndexOf("}");
  // Truncation can precede every closing brace — repair the whole tail then.
  const seg = end > a ? raw.slice(a, end + 1) : raw.slice(a);
  return lenientParse(structuralRepair(fixTokenGlitches(fixInvalidEscapes(seg))));
}

export type ParseOutcome = "ok" | "repaired" | "failed";

/**
 * Strict parse, then the repair pipeline. Returns the outcome so callers can
 * trace how often the model's JSON needed fixing.
 */
export function parseModelJSON(raw: string): { data: any; parse: ParseOutcome } {
  try {
    const data = extractJSON(raw);
    if (data != null) return { data, parse: "ok" };
  } catch { /* fall through to repair */ }
  const repaired = robustJSON(raw);
  return repaired != null ? { data: repaired, parse: "repaired" } : { data: null, parse: "failed" };
}

/** Decomposition parse with a last resort: drop the (possibly mangled) final decision and re-close. */
export function parseDecomposition(raw: string): any {
  const direct = robustJSON(raw);
  if (direct) return direct;
  const a = raw.indexOf("{");
  const t = structuralRepair(raw.slice(a, raw.lastIndexOf("}") + 1));
  const cut = t.lastIndexOf("},{");
  if (cut > 0) return lenientParse(structuralRepair(t.slice(0, cut + 1)));
  return null;
}

/**
 * Quote-repair can merge a malformed region's key/value text into a string
 * value ("Security":"question":"…) — keep only the part before the artifact.
 */
export function scrubRepairArtifacts(s: unknown, fallback: string): string {
  const first = String(s ?? "").split(/"\s*[:,]\s*"?/)[0] ?? "";
  return tidyProse(first.replace(/^"+|"+$/g, "")) || fallback;
}
